// Elevation calculation service for determining effective ground level at any point
// Used by BuildingRenderer and PlantRenderer to position objects on elevated surfaces

import type { GroundShape, Building, Product, Point } from '@/types'
import { debug } from '@/utils/debug'

/**
 * Axis-aligned bounding box for fast pre-check
 */
interface BoundingBox {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}

/**
 * Internal representation of an object that can elevate other objects
 */
interface ElevatedObject {
  id: string
  elevation: number
  effectiveElevation: number  // For buildings: groundElevation + ownElevation
  bounds: BoundingBox
  shape: 'rectangle' | 'ellipse' | 'polygon' | 'building'
  center: { x: number, z: number }
  width: number   // In editor units (dots)
  depth: number   // In editor units (dots)
  rotation: number // In degrees
  points?: Point[] // For polygons only (local coordinates)
}

/**
 * Service for calculating effective elevation at any point in the garden.
 *
 * Elevation inheritance order: ground → buildings → plants
 * - Ground shapes have their own elevation
 * - Buildings inherit max ground elevation + their own elevation
 * - Plants inherit max(ground, building) elevation
 *
 * Only objects with elevation > 0 are tracked for performance.
 */
export class ElevationService {
  private groundObjects: ElevatedObject[] = []
  private buildingObjects: ElevatedObject[] = []

  /**
   * Register ground shapes for elevation calculation.
   * Only shapes with elevation > 0 are tracked.
   */
  setGroundShapes(shapes: GroundShape[], _products: Map<string, Product>): void {
    this.groundObjects = []

    for (const shape of shapes) {
      if (shape.markedForDestruction) continue
      if (shape.elevation <= 0) continue

      // Skip invalid shapes
      if (shape.width <= 0 || shape.height <= 0) continue
      if (shape.type === 'polygon' && (!shape.points || shape.points.length < 3)) continue

      const center = {
        x: shape.x + shape.width / 2,
        z: shape.y + shape.height / 2
      }

      const obj: ElevatedObject = {
        id: shape.id,
        elevation: shape.elevation,
        effectiveElevation: shape.elevation,
        bounds: this.calculateRotatedBounds(center, shape.width, shape.height, shape.rotation),
        shape: shape.type,
        center,
        width: shape.width,
        depth: shape.height,
        rotation: shape.rotation,
        points: shape.points
      }

      this.groundObjects.push(obj)
    }

    debug.log('Elevation', `Registered ${this.groundObjects.length} elevated ground shapes`)
  }

  /**
   * Register buildings for elevation calculation (used by plants).
   * Only buildings with elevation > 0 are tracked.
   *
   * Note: Building placement Y = groundElevation only.
   * The building's own elevation is a modifier for plants, stored as effectiveElevation.
   * effectiveElevation = groundElevation + building.elevation
   */
  setBuildings(buildings: Building[], products: Map<string, Product>): void {
    this.buildingObjects = []

    for (const building of buildings) {
      if (building.markedForDestruction) continue
      if (building.elevation <= 0) continue

      // Get dimensions from product or building
      const product = building.productId ? products.get(building.productId) : undefined
      const width = (product?.width ?? building.width) || 100
      const depth = (product?.height ?? building.height) || 100

      // Skip invalid buildings
      if (width <= 0 || depth <= 0) continue

      const center = {
        x: building.x + width / 2,
        z: building.y + depth / 2
      }

      // Calculate effective elevation (ground beneath + own elevation)
      const groundElevation = this.getGroundElevation(center.x, center.z)
      const effectiveElevation = groundElevation + building.elevation

      const obj: ElevatedObject = {
        id: building.id,
        elevation: building.elevation,
        effectiveElevation,
        bounds: this.calculateRotatedBounds(center, width, depth, building.rotation),
        shape: 'building',
        center,
        width,
        depth,
        rotation: building.rotation
      }

      this.buildingObjects.push(obj)
    }

    debug.log('Elevation', `Registered ${this.buildingObjects.length} elevated buildings`)
  }

  /**
   * Get the ground elevation at a point (for building positioning).
   * Returns max elevation of all ground shapes containing the point.
   */
  getGroundElevation(x: number, z: number): number {
    let maxElevation = 0

    for (const obj of this.groundObjects) {
      // Fast bounding box pre-check
      if (!this.isPointInBounds(x, z, obj.bounds)) continue

      // Exact geometry check
      if (this.isPointInShape(x, z, obj)) {
        maxElevation = Math.max(maxElevation, obj.elevation)
      }
    }

    return maxElevation
  }

  /**
   * Get the effective elevation at a point (for plant positioning).
   * Returns max of ground elevation and building effective elevation.
   */
  getElevationForPlant(x: number, z: number): number {
    // Get ground elevation
    let maxElevation = this.getGroundElevation(x, z)

    // Check buildings
    for (const obj of this.buildingObjects) {
      // Fast bounding box pre-check
      if (!this.isPointInBounds(x, z, obj.bounds)) continue

      // Exact geometry check (buildings are rectangles)
      if (this.isPointInRotatedRectangle(x, z, obj)) {
        maxElevation = Math.max(maxElevation, obj.effectiveElevation)
      }
    }

    return maxElevation
  }

  /**
   * Clear all registered objects
   */
  clear(): void {
    this.groundObjects = []
    this.buildingObjects = []
  }

  // --- Private geometry methods ---

  /**
   * Calculate axis-aligned bounding box for a rotated rectangle
   */
  private calculateRotatedBounds(
    center: { x: number, z: number },
    width: number,
    depth: number,
    rotation: number
  ): BoundingBox {
    const rad = rotation * Math.PI / 180
    const cos = Math.abs(Math.cos(rad))
    const sin = Math.abs(Math.sin(rad))
    const halfW = (width * cos + depth * sin) / 2
    const halfD = (width * sin + depth * cos) / 2

    return {
      minX: center.x - halfW,
      maxX: center.x + halfW,
      minZ: center.z - halfD,
      maxZ: center.z + halfD
    }
  }

  /**
   * Fast check if point is within axis-aligned bounding box
   */
  private isPointInBounds(x: number, z: number, bounds: BoundingBox): boolean {
    return x >= bounds.minX && x <= bounds.maxX && z >= bounds.minZ && z <= bounds.maxZ
  }

  /**
   * Check if point is inside a shape (dispatches to specific geometry check)
   */
  private isPointInShape(x: number, z: number, obj: ElevatedObject): boolean {
    switch (obj.shape) {
      case 'rectangle':
      case 'building':
        return this.isPointInRotatedRectangle(x, z, obj)
      case 'ellipse':
        return this.isPointInRotatedEllipse(x, z, obj)
      case 'polygon':
        return this.isPointInPolygon(x, z, obj)
      default:
        return false
    }
  }

  /**
   * Check if point is inside a rotated rectangle
   * Transform point into local (unrotated) coordinate space, then check bounds
   */
  private isPointInRotatedRectangle(x: number, z: number, obj: ElevatedObject): boolean {
    // Transform point into local coordinate space (rotate around center)
    const rad = -obj.rotation * Math.PI / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const dx = x - obj.center.x
    const dz = z - obj.center.z
    const localX = dx * cos - dz * sin
    const localZ = dx * sin + dz * cos

    // Check against half-dimensions
    return Math.abs(localX) <= obj.width / 2 && Math.abs(localZ) <= obj.depth / 2
  }

  /**
   * Check if point is inside a rotated ellipse
   * Transform point into local space, then apply ellipse equation
   */
  private isPointInRotatedEllipse(x: number, z: number, obj: ElevatedObject): boolean {
    // Transform point into local coordinate space
    const rad = -obj.rotation * Math.PI / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const dx = x - obj.center.x
    const dz = z - obj.center.z
    const localX = dx * cos - dz * sin
    const localZ = dx * sin + dz * cos

    // Ellipse equation: (x/rx)^2 + (z/rz)^2 <= 1
    const rx = obj.width / 2
    const rz = obj.depth / 2
    if (rx <= 0 || rz <= 0) return false

    return (localX * localX) / (rx * rx) + (localZ * localZ) / (rz * rz) <= 1
  }

  /**
   * Check if point is inside a polygon using ray-casting algorithm
   * Points are in local coordinates relative to shape origin
   */
  private isPointInPolygon(x: number, z: number, obj: ElevatedObject): boolean {
    if (!obj.points || obj.points.length < 3) return false

    // Convert world point to local polygon coordinates
    // Polygon points are relative to shape's top-left corner (x, y in editor coords)
    // Shape center is at (x + width/2, y + height/2)
    // So top-left is at (center.x - width/2, center.z - depth/2)
    const localX = x - (obj.center.x - obj.width / 2)
    const localZ = z - (obj.center.z - obj.depth / 2)

    // Ray-casting algorithm: count intersections with polygon edges
    // A point is inside if it crosses an odd number of edges
    const points = obj.points
    const n = points.length
    let inside = false

    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = points[i].x
      const yi = points[i].y  // Note: polygon uses y, we're checking z dimension
      const xj = points[j].x
      const yj = points[j].y

      // Check if ray from (localX, localZ) to the right intersects this edge
      const intersect = ((yi > localZ) !== (yj > localZ)) &&
        (localX < (xj - xi) * (localZ - yi) / (yj - yi) + xi)

      if (intersect) inside = !inside
    }

    return inside
  }
}

// Singleton instance for use across renderers
export const elevationService = new ElevationService()
